import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { injectIntl, intlShape } from 'react-intl';
import {
  Utils,
  Icon,
  Select as SelectMW,
  MenuItem,
  Button as ButtonMW,
  Label as LabelMW,
  Checkbox,
  Divider,
  Tooltip,
} from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import cn from 'classnames';
import Edit from '../Edit';
import Select from '../Select';
import MultiSelect from '../MultiSelect';
import Label from '../Label';
import Calendar from '../Calendar';
import Check from '../Check';
import Link from '../Link';
import Image from '../Image';
import Upload from '../Upload';
import Button from '../Button';
import { getItemAvailableFields } from '../../../../SetDefaultValue';
import mes from '../../intl';
import './BaseItem.scss';

const _ITEMS_ = {
  Edit,
  Select,
  MultiSelect,
  Label,
  Calendar,
  Check,
  Link,
  Image,
  Upload,
  Button,
  Radio: Select,
};

const classes = [
  {
    item: 'MenuItem',
    className: 'check',
    icon: 'checkbox_on',
  },
  {
    item: 'MenuItem',
    className: 'radio',
    icon: 'radio_on',
  },
  {
    item: 'MenuItem',
    className: 'select',
    icon: 'dropdown',
  },
  {
    item: 'MenuItem',
    className: 'multiSelect',
    icon: 'list',
  },
  {
    item: 'Divider',
  },
  {
    item: 'MenuItem',
    className: 'edit',
    icon: 'textfield',
  },
  {
    item: 'MenuItem',
    className: 'label',
    icon: 'paragraph',
  },
  {
    item: 'MenuItem',
    className: 'link',
    icon: 'link',
  },
  {
    item: 'Divider',
  },
  {
    item: 'MenuItem',
    className: 'button',
    icon: 'button',
  },
  {
    item: 'MenuItem',
    className: 'image',
    icon: 'picture',
  },
  {
    item: 'MenuItem',
    className: 'upload',
    icon: 'attach',
  },
  {
    item: 'MenuItem',
    className: 'calendar',
    icon: 'calendar',
  },
];

const _CLASSES_ = {
  MenuItem,
  Divider,
};

/**
 * Компонент BaseItem
 */
class BaseItem extends PureComponent {
  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleMakeActive = this.handleMakeActive.bind(this);
    this.actualizeValue = this.actualizeValue.bind(this);
  }

  // Ф-ция локализации
  i(mesId, values) {
    return AppUtils.getMes(this.context)(mesId, values);
  }

  // Актуализация value после смены класса элемента формы
  actualizeValue(className) {
    let value;
    switch (className) {
      case 'button':
        return;
      case 'check':
        value = false;
        break;
      case 'upload':
      case 'calendar':
        value = {};
        break;
      case 'multiSelect':
        value = [];
        break;
      default:
        value = '';
        break;
    }
    this.props.onChange({ id: 'value', value });
  }

  handleChange({ fieldId, value, errors }) {
    this.props.onChange({ id: fieldId, value, errors });
  }

  handleMakeActive() {
    const { sectionIndex, itemIndex, onActive } = this.props;
    onActive({ sectionIndex, itemIndex });
  }

  checkIsActive({ sectionIndex, itemIndex }) {
    const { activeSection, activeItem } = this.props;
    return activeSection === sectionIndex && activeItem === itemIndex;
  }

  getRowItemIndex() {
    const { row, itemIndex, rowItems } = this.props;
    if (!row) return { minIndex: itemIndex, maxIndex: itemIndex };
    const sortRowItems = Utils.sort(rowItems.slice(), 'itemIndex');
    const minIndex = sortRowItems[0].itemIndex;
    const maxIndex = sortRowItems[sortRowItems.length - 1].itemIndex;
    return { minIndex, maxIndex };
  }

  getSelectIcon(type) {
    const item = classes.find((i) => i.className === type);
    return item ? item.icon : null;
  }

  renderClasses(type) {
    return (
      <SelectMW
        styleName="item__active__header__classSelect"
        bordered={true}
        value={type}
        leftIcon={this.getSelectIcon(type)}
        onChange={({ value }) => {
          this.handleChange({ fieldId: 'class', value });
          this.actualizeValue(value);
        }}
      >
        {classes.map((i, index) => {
          const Component = _CLASSES_[i.item]; // NOSONAR
          return (
            <Component
              key={i.className || index}
              value={i.className}
              label={this.i(mes[`itemClass_${i.className}`])}
              leftIcon={i.icon}
            />
          );
        })}
      </SelectMW>
    );
  }

  // Отрисовка самого элемента
  renderItem(item, focus = false, isActive = false) {
    const { sectionIndex, itemIndex, class: className } = item;
    const ItemName = Utils.toUpperLatter(className);
    const Item = _ITEMS_[ItemName];
    if (!Item) return null;
    return (
      <Item
        key={`${sectionIndex}_${itemIndex}`}
        className={className}
        onChange={this.handleChange}
        title={item.title}
        placeholder={item.placeholder}
        options={item.options}
        value={item.value}
        errors={item.errors}
        id={item.id}
        isActive={isActive}
        autoFocus={focus}
        extra={item.extra}
      />
    );
  }

  // Отрисовка неактивного элемента
  renderUnactiveItem() {
    const { class: className, row, activeRow } = this.props;
    const ItemName = Utils.toUpperLatter(className);
    const Item = _ITEMS_[ItemName];
    if (!Item) return null;
    // Не рисуем неактивный элемент в строке
    if (row && row === activeRow) return null;
    return (
      <div styleName="item__unactive" onClick={this.handleMakeActive}>
        {this.renderItem(this.props)}
      </div>
    );
  }

  // Отрисовать табы для строки
  renderRowTabs() {
    const { row, activeRow, rowItems, onActive } = this.props;
    if (!row || row !== activeRow) return null;
    const tabs = rowItems.map((item, index) => {
      const { title, sectionIndex, itemIndex } = item;
      const isActive = this.checkIsActive({ sectionIndex, itemIndex });
      return (
        <div
          key={item.id}
          styleName={cn('item__active__tabs__item', { active: isActive })}
          onClick={() => onActive({ sectionIndex, itemIndex })}
        >
          {title || `${this.i(mes.item)} ${index + 1}`}
        </div>
      );
    });
    return <div styleName="item__active__tabs">{tabs}</div>;
  }

  // Отрисовка активного элемента
  renderActiveItem() {
    const {
      class: className,
      required,
      sectionIndex,
      itemIndex,
      sectionsLen,
      itemsLen,
      rowCount,
      row,
      activeRow,
      onCreateItem,
      onRemoveItem,
      onSortItem,
      onShowPanel,
    } = this.props;
    const actionsDinied = sectionsLen === 1 && itemsLen === 1;
    const isActiveRow = row && row === activeRow;
    const requiredIsVar =
      required && !['true', 'false'].includes(required.toString());
    const harRequiredField = getItemAvailableFields(this.props).includes(
      'required'
    );
    const { minIndex, maxIndex } = this.getRowItemIndex();
    return (
      <div
        styleName={cn('item__active', { row: isActiveRow })}
        className="active"
      >
        {this.renderRowTabs()}
        <div styleName="item__active__header">
          <div styleName="item__active__header__left">
            <div styleName="item__active__header__sort">
              <div
                onClick={() =>
                  onSortItem({ sectionIndex, itemIndex, position: 'up' })
                }
              >
                <Icon
                  type="arrow"
                  visibility={
                    actionsDinied || minIndex === 0 ? 'disabled' : 'visible'
                  }
                />
              </div>
              <div
                onClick={() =>
                  onSortItem({ sectionIndex, itemIndex, position: 'down' })
                }
              >
                <Icon
                  type="arrow"
                  visibility={
                    actionsDinied || maxIndex === itemsLen - 1
                      ? 'disabled'
                      : 'visible'
                  }
                />
              </div>
            </div>
            <div styleName="item__active__header__type">
              {this.renderClasses(className)}
            </div>
            {harRequiredField ? (
              <div
                styleName={cn('item__active__header__required', {
                  disabled: requiredIsVar,
                })}
              >
                <Checkbox
                  value={AppUtils.toBool(required)}
                  visibility={requiredIsVar ? 'disabled' : 'visible'}
                  onChange={({ value }) =>
                    this.handleChange({
                      fieldId: 'required',
                      value: value.toString(),
                    })
                  }
                >
                  <LabelMW value={this.i(mes.required)} />
                </Checkbox>
              </div>
            ) : null}
          </div>
          <div styleName="item__active__header__right">
            <div onClick={onShowPanel}>
              <Icon type="settings" />
            </div>
            <div onClick={() => onRemoveItem({ sectionIndex, itemIndex })}>
              <Icon type="trash" visibility="visible" />
            </div>
          </div>
        </div>
        <div styleName="item__active__main">
          {this.renderItem(this.props, true, true)}
        </div>
        <div styleName="item__active__footer">
          <Tooltip
            value={this.i(mes.addItemDown)}
            styleName="item__active__footer__tooltip"
          >
            <ButtonMW
              icon="add_item_below"
              type="default"
              size="medium"
              fontWeight="normal"
              rounded="true"
              onClick={() =>
                onCreateItem({ sectionIndex, itemIndex, isRow: false })
              }
            />
          </Tooltip>
          <Tooltip
            value={this.i(mes.addItemRow)}
            styleName="item__active__footer__tooltip"
          >
            <ButtonMW
              icon="add_item_row"
              type="default"
              size="medium"
              fontWeight="normal"
              visibility={rowCount < 3 ? 'visible' : 'hidden'}
              rounded="true"
              onClick={() =>
                onCreateItem({ sectionIndex, itemIndex, isRow: true })
              }
            />
          </Tooltip>
        </div>
      </div>
    );
  }

  render() {
    const { sectionIndex, itemIndex } = this.props;
    const isActive = this.checkIsActive({ sectionIndex, itemIndex });
    return (
      <div styleName={`item${sectionIndex === -1 ? '__system' : ''}`}>
        {isActive ? this.renderActiveItem() : this.renderUnactiveItem()}
      </div>
    );
  }
}

BaseItem.defaultProps = {
  value: '',
};

BaseItem.propTypes = {
  activeSection: PropTypes.number.isRequired,
  activeItem: PropTypes.number.isRequired,
  activeRow: PropTypes.string,
  sectionIndex: PropTypes.number.isRequired,
  itemIndex: PropTypes.number.isRequired,
  class: PropTypes.string.isRequired,
  row: PropTypes.string,
  rowItems: PropTypes.array,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rowCount: PropTypes.number,
  sectionsLen: PropTypes.number,
  itemsLen: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onActive: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  onSortItem: PropTypes.func.isRequired,
  onShowPanel: PropTypes.func.isRequired,
};

BaseItem.contextTypes = {
  intl: intlShape,
};

export default injectIntl(BaseItem);
